import * as v from "valibot";

const PreferencesSchema = v.object({
  ringtone: v.boolean(),
  effects: v.boolean(),
  volume: v.pipe(v.number(), v.minValue(0), v.maxValue(1)),
});
type Preferences = v.InferOutput<typeof PreferencesSchema>;
const storageKey = "ranna-sounds:v1";

function readPreferences(): Preferences {
  try {
    const saved = localStorage.getItem(storageKey);
    if (saved) return v.parse(PreferencesSchema, JSON.parse(saved));
  } catch {
    // Unavailable storage or an invalid saved choice leaves the defaults usable.
  }
  return {
    ringtone: true,
    effects:
      typeof matchMedia !== "function" ||
      !matchMedia("(prefers-reduced-motion: reduce)").matches,
    volume: 0.5,
  };
}

// Related sine tones with soft attacks, short tails and room for speech.
// Each tuple is frequency, start offset, duration and peak gain.
const notes = {
  incoming: [
    [523, 0, 0.28, 0.15],
    [659, 0.22, 0.34, 0.12],
  ],
  outgoing: [
    [392, 0, 0.7, 0.07],
    [494, 0, 0.7, 0.045],
  ],
  connected: [
    [523, 0, 0.1, 0.07],
    [659, 0.09, 0.13, 0.06],
  ],
  ended: [
    [523, 0, 0.12, 0.08],
    [392, 0.11, 0.18, 0.07],
  ],
  reconnecting: [
    [392, 0, 0.13, 0.09],
    [392, 0.21, 0.13, 0.07],
  ],
  restored: [
    [494, 0, 0.1, 0.07],
    [659, 0.1, 0.15, 0.06],
  ],
  message: [[784, 0, 0.13, 0.08]],
} satisfies Record<string, [number, number, number, number][]>;
export type Sound = keyof typeof notes;
type Channel = "call" | "group" | "message" | "preview";
const isRingtone = (sound: Sound) =>
  sound === "incoming" || sound === "outgoing";

class SoundEffects {
  private context: AudioContext | null = null;
  private preferences = readPreferences();
  private listeners = new Set<() => void>();
  private playing = new Map<Channel, { sound: Sound; stop: () => void }>();
  private unlocked = new Set<() => void>();
  private lastMessage = -Infinity;

  getPreferences = () => this.preferences;
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  private applyPreferences(preferences: Preferences) {
    const volumeChanged = this.preferences.volume !== preferences.volume;
    this.preferences = preferences;
    // Silence playing and already scheduled notes immediately when muted.
    for (const [channel, { sound }] of this.playing)
      if (volumeChanged || !this.enabled(sound)) this.stop(channel);
    this.listeners.forEach((listener) => listener());
  }
  setPreferences(changes: Partial<Preferences>) {
    const preferences = v.parse(PreferencesSchema, {
      ...this.preferences,
      ...changes,
    });
    this.applyPreferences(preferences);
    try {
      localStorage.setItem(storageKey, JSON.stringify(preferences));
    } catch {
      /* The choice still works when storage is disabled. */
    }
  }
  private enabled(sound: Sound) {
    return (
      this.preferences.volume > 0 &&
      (isRingtone(sound) ? this.preferences.ringtone : this.preferences.effects)
    );
  }
  onUnlock(listener: () => void) {
    this.unlocked.add(listener);
    return () => {
      this.unlocked.delete(listener);
    };
  }
  // Only gestures resume audio. One-shot cues are never queued for later replay.
  unlock = () => {
    try {
      const created = !this.context || this.context.state === "closed";
      if (!this.context || this.context.state === "closed")
        this.context = new AudioContext();
      const context = this.context;
      if (context.state === "running") {
        if (created) this.unlocked.forEach((listener) => listener());
        return;
      }
      void context
        .resume()
        .then(() => {
          if (this.context === context && context.state === "running")
            this.unlocked.forEach((listener) => listener());
        })
        .catch(() => {});
    } catch {
      /* Unsupported or blocked audio must not interrupt the app. */
    }
  };
  listen() {
    const storage = (event: StorageEvent) => {
      if (event.key === storageKey || event.key === null)
        this.applyPreferences(readPreferences());
    };
    document.addEventListener("pointerdown", this.unlock);
    document.addEventListener("keydown", this.unlock);
    window.addEventListener("storage", storage);
    return () => {
      document.removeEventListener("pointerdown", this.unlock);
      document.removeEventListener("keydown", this.unlock);
      window.removeEventListener("storage", storage);
      for (const channel of this.playing.keys()) this.stop(channel);
      const context = this.context;
      this.context = null;
      if (context && context.state !== "closed")
        void context.close().catch(() => {});
      this.lastMessage = -Infinity;
    };
  }
  stop(channel: Channel = "call") {
    this.playing.get(channel)?.stop();
    this.playing.delete(channel);
  }
  play(sound: Sound, channel: Channel = "call") {
    const context = this.context;
    if (!this.enabled(sound) || !context || context.state !== "running")
      return false;
    this.stop(channel);
    const now = context.currentTime;
    const nodes: { oscillator: OscillatorNode; gain: GainNode }[] = [];
    const playback = {
      sound,
      stop: () => {
        for (const { oscillator, gain } of nodes) {
          try {
            oscillator.stop();
          } catch {
            /* A failed start has nothing to stop. */
          }
          oscillator.disconnect();
          gain.disconnect();
        }
      },
    };
    this.playing.set(channel, playback);
    let remaining = notes[sound].length;
    try {
      for (const [frequency, offset, duration, peak] of notes[sound]) {
        const oscillator = context.createOscillator(),
          gain = context.createGain();
        nodes.push({ oscillator, gain });
        const start = now + offset;
        oscillator.type = "sine";
        oscillator.frequency.setValueAtTime(frequency, start);
        if (sound === "message")
          oscillator.frequency.exponentialRampToValueAtTime(
            frequency * 0.78,
            start + 0.08,
          );
        gain.gain.setValueAtTime(0, now);
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(
          peak * this.preferences.volume,
          start + 0.015,
        );
        gain.gain.exponentialRampToValueAtTime(0.001, start + duration);
        gain.gain.linearRampToValueAtTime(0, start + duration + 0.01);
        oscillator.connect(gain);
        gain.connect(context.destination);
        oscillator.onended = () => {
          oscillator.disconnect();
          gain.disconnect();
          if (--remaining === 0 && this.playing.get(channel) === playback)
            this.playing.delete(channel);
        };
        oscillator.start(start);
        oscillator.stop(start + duration + 0.02);
      }
    } catch {
      this.stop(channel);
      return false;
    }
    return true;
  }
  message() {
    // A burst of messages gets one cue; history refetches never call this method.
    if (Date.now() - this.lastMessage < 2000) return false;
    const played = this.play("message", "message");
    if (played) this.lastMessage = Date.now();
    return played;
  }
}

export const sounds = new SoundEffects();
